import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Paperclip, Eye, Download, Trash, FloppyDisk, X } from "@phosphor-icons/react";
import api, { apiErrorMessage } from "@/lib/api";
import ChangeLogNotes from "@/components/shared/ChangeLogNotes";

const ATTACHMENT_SECTIONS = [
  { id: "certificate_of_good_standing",  label: "Certificate of Good Standing" },
  { id: "ss4_filing_articles_of_org",    label: "SS4 / Filing Receipt / Articles of Organization" },
  { id: "operating_agreement",           label: "Operating Agreement" },
  { id: "additional_documents",          label: "Additional Documents" },
];

const FIELDS = [
  { id: "entity",                              label: "Entity" },
  { id: "ein",                                 label: "EIN" },
  { id: "dos",                                 label: "DOS" },
  { id: "creation_date",                       label: "Creation Date", type: "date" },
  { id: "entity_state",                        label: "Entity State" },
  { id: "manager",                             label: "Manager" },
  { id: "owned_real_estate_asset_1",           label: "Owned Real Estate Asset 1" },
  { id: "owned_real_estate_asset_2",           label: "Owned Real Estate Asset 2" },
  { id: "nys_biennial_last_renewal_date",      label: "NYS Biennial Last Renewal Date", type: "date" },
  { id: "biennial_expiration",                 label: "Biennial Expiration", type: "date" },
  { id: "nys_department_of_labor_er",          label: "NYS Department of Labor ER" },
  { id: "nybe_id",                             label: "NYBE ID" },
];

function _fmtDateMDY(s) {
  if (!s) return "—";
  const d = new Date(String(s).length <= 10 ? String(s) + "T00:00:00" : String(s));
  if (Number.isNaN(d.getTime())) return "—";
  return `${String(d.getMonth() + 1).padStart(2, "0")}/${String(d.getDate()).padStart(2, "0")}/${d.getFullYear()}`;
}

export default function EinModal({ entity, customCols = [], onClose, onChange }) {
  const [form, setForm] = useState(null);
  const [attachments, setAttachments] = useState({});
  const [uploading, setUploading] = useState("");
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const fileRefs = useRef({});

  useEffect(() => { setForm(entity ? { ...entity } : null); setDirty(false); }, [entity]);

  const loadAtt = useCallback(async (eid) => {
    try { const { data } = await api.get(`/ein/${eid}/attachments`); setAttachments(data || {}); }
    catch { /* ignore */ }
  }, []);
  useEffect(() => { if (entity?.id) loadAtt(entity.id); }, [entity?.id, loadAtt]);

  if (!entity || !form) return null;
  const setField = (k, v) => { setForm((f) => ({ ...f, [k]: v })); setDirty(true); };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      ["id","entity_seq","created_at","updated_at","created_by_id","created_by_name","updated_by_id","updated_by_name","has_attachments"].forEach((k) => delete payload[k]);
      const { data } = await api.put(`/ein/${entity.id}`, payload);
      onChange && onChange(data);
      setDirty(false);
      toast.success("Entity saved");
    } catch (e) { const m = apiErrorMessage(e); if (m) toast.error(m); }
    finally { setSaving(false); }
  };

  const upload = async (section, file) => {
    if (!file) return;
    setUploading(section);
    try {
      const fd = new FormData(); fd.append("file", file);
      await api.post(`/ein/${entity.id}/attachments?section=${section}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Uploaded to ${section.replace(/_/g, " ")}`);
      await loadAtt(entity.id);
    } catch (e) { const m = apiErrorMessage(e); if (m) toast.error(m); }
    finally { setUploading(""); }
  };

  const remove = async (fid) => {
    try { await api.delete(`/ein/${entity.id}/attachments/${fid}`); await loadAtt(entity.id); }
    catch (e) { const m = apiErrorMessage(e); if (m) toast.error(m); }
  };

  const del = async () => {
    if (!window.confirm(`Delete entity #${entity.entity_seq}?`)) return;
    try { await api.delete(`/ein/${entity.id}`); toast.success("Entity deleted"); onClose(); }
    catch (e) { const m = apiErrorMessage(e); if (m) toast.error(m); }
  };

  const backend = process.env.REACT_APP_BACKEND_URL;
  const attachUrl = (fid, dl) => `${backend}/api/ein/${entity.id}/attachments/${fid}${dl ? "?download=true" : ""}`;
  const kpis = [
    { label: "Entity", value: form.entity || "—" },
    { label: "EIN", value: form.ein || "—" },
    { label: "Biennial Expiration", value: _fmtDateMDY(form.biennial_expiration) },
  ];

  return (
    <Dialog open={!!entity} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-6xl" data-testid="ein-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between">
            <span>Entity #{entity.entity_seq}{form.entity ? ` · ${form.entity}` : ""}</span>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={del} className="text-rose-600 border-rose-200 hover:bg-rose-50">
                <Trash size={12} className="mr-1" /> Delete
              </Button>
              <Button size="sm" onClick={save} disabled={saving || !dirty} className="bg-[#1f64c2] hover:bg-[#1854a3]" data-testid="ein-modal-save">
                <FloppyDisk size={12} className="mr-1" /> {saving ? "Saving…" : "Save"}
              </Button>
            </div>
          </DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_320px] gap-4">
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-2" data-testid="ein-modal-kpis">
              {kpis.map((k) => (
                <div key={k.label} className="rounded-lg border border-slate-200 bg-white px-2.5 py-1.5">
                  <div className="text-[9px] uppercase tracking-widest text-slate-500 font-semibold">{k.label}</div>
                  <div className="text-sm font-bold text-slate-900 mt-0.5 truncate" title={String(k.value)}>{k.value}</div>
                </div>
              ))}
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              {FIELDS.map((f) => (
                <label key={f.id} className="block text-[10px]">
                  <span className="uppercase tracking-widest text-slate-500 font-semibold">{f.label}</span>
                  <Input type={f.type || "text"} value={form[f.id] ?? ""} onChange={(e) => setField(f.id, e.target.value)} className="text-xs h-7 mt-0.5" />
                </label>
              ))}
            </div>

            {customCols.length > 0 && (
              <div>
                <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mt-2 mb-2">Custom fields</div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  {customCols.map((c) => (
                    <label key={c.id} className="block text-[10px]">
                      <span className="uppercase tracking-widest text-slate-500 font-semibold">{c.label}</span>
                      <Input
                        type={c.type === "date" ? "date" : (c.type === "number" || c.type === "money" || c.type === "percent") ? "number" : "text"}
                        value={(form.custom_fields || {})[c.key] ?? ""}
                        onChange={(e) => setField("custom_fields", { ...(form.custom_fields || {}), [c.key]: (c.type === "text" || c.type === "date") ? (e.target.value || "") : (e.target.value === "" ? null : Number(e.target.value)) })}
                        className="text-xs h-7 mt-0.5"
                      />
                    </label>
                  ))}
                </div>
              </div>
            )}

            <div>
              <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mt-2 mb-2">Attachments</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2" data-testid="ein-modal-attachments">
                {ATTACHMENT_SECTIONS.map((s) => {
                  const versions = attachments[s.id] || [];
                  const current = versions[0];
                  const prev = versions.slice(1);
                  return (
                    <div key={s.id} className="rounded-md border border-slate-200 bg-slate-50/50 p-2" data-testid={`ein-attach-section-${s.id}`}>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-[10px] font-bold text-slate-700 truncate" title={s.label}>{s.label}</span>
                        <button type="button" onClick={() => fileRefs.current[s.id]?.click()} disabled={uploading === s.id}
                          className="text-[10px] font-semibold text-[#1f64c2] hover:text-[#327de3] inline-flex items-center gap-1 px-1.5 py-0.5 rounded border border-slate-200 bg-white"
                          data-testid={`ein-attach-add-${s.id}`}>
                          <Paperclip size={10} /> {uploading === s.id ? "Uploading…" : "Add file"}
                        </button>
                        <input ref={(el) => { fileRefs.current[s.id] = el; }} type="file" className="hidden"
                          onChange={(e) => { const f = e.target.files?.[0]; if (f) upload(s.id, f); e.target.value = ""; }} />
                      </div>
                      {!current ? <div className="text-[10px] text-slate-400 italic">No files uploaded.</div> : (
                        <div className="flex items-center gap-1 text-[10px] bg-white border border-slate-200 rounded px-1.5 py-0.5">
                          <span className="font-semibold text-slate-500">v{current.version}</span>
                          <span className="truncate flex-1" title={current.filename}>{current.filename}</span>
                          <a href={attachUrl(current.id)} target="_blank" rel="noreferrer" className="text-[#1f64c2] hover:text-[#327de3]"><Eye size={10} /></a>
                          <a href={attachUrl(current.id, true)} className="text-[#1f64c2] hover:text-[#327de3]"><Download size={10} /></a>
                          <button onClick={() => remove(current.id)} className="text-rose-500 hover:text-rose-700"><Trash size={10} /></button>
                        </div>
                      )}
                      {prev.length > 0 && (
                        <details className="mt-1">
                          <summary className="text-[9px] uppercase tracking-widest font-semibold text-slate-500 cursor-pointer">Previous ({prev.length})</summary>
                          <div className="mt-0.5 space-y-0.5 pl-2 border-l border-slate-200">
                            {prev.map((v) => (
                              <div key={v.id} className="flex items-center gap-1 text-[9px]">
                                <span className="font-semibold text-slate-500">v{v.version}</span>
                                <span className="truncate flex-1" title={v.filename}>{v.filename}</span>
                                <a href={attachUrl(v.id)} target="_blank" rel="noreferrer" className="text-[#1f64c2]"><Eye size={9} /></a>
                                <a href={attachUrl(v.id, true)} className="text-[#1f64c2]"><Download size={9} /></a>
                                <button onClick={() => remove(v.id)} className="text-rose-500"><Trash size={9} /></button>
                              </div>
                            ))}
                          </div>
                        </details>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          <ChangeLogNotes basePath={`/ein/${entity.id}`} idFieldLabel="entity" />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}><X size={13} className="mr-1" /> Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
